/**
 * Messaging Hooks
 * React Query hooks for attorney-client messaging
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import messagingService from '../services/messagingService';

// Query keys
export const messagingKeys = {
  all: ['messaging'],
  messages: () => [...messagingKeys.all, 'messages'],
  messagesList: (filters) => [...messagingKeys.messages(), filters],
  pending: () => [...messagingKeys.messages(), 'pending'],
  thread: (threadId) => [...messagingKeys.messages(), 'thread', threadId],
  application: (appId) => [...messagingKeys.messages(), 'application', appId],
  message: (id) => [...messagingKeys.messages(), id],
  internalRequests: () => [...messagingKeys.all, 'internal-requests'],
  internalRequestsList: (status) => [...messagingKeys.internalRequests(), status],
  myRequests: () => [...messagingKeys.all, 'my-requests'],
  forwardedResponses: () => [...messagingKeys.all, 'forwarded-responses'],
};

// =============================================================================
// Messages Queries
// =============================================================================

/**
 * Get all messages for current user
 */
export function useMessages(options = {}) {
  return useQuery({
    queryKey: messagingKeys.messagesList(options),
    queryFn: () => messagingService.getMessages(options),
  });
}

/**
 * Get messages awaiting client response
 */
export function usePendingMessages() {
  return useQuery({
    queryKey: messagingKeys.pending(),
    queryFn: () => messagingService.getPendingMessages(),
  });
}

/**
 * Get a single message
 */
export function useMessage(id) {
  return useQuery({
    queryKey: messagingKeys.message(id),
    queryFn: () => messagingService.getMessageById(id),
    enabled: !!id,
  });
}

/**
 * Get messages in a thread
 */
export function useMessageThread(threadId) {
  return useQuery({
    queryKey: messagingKeys.thread(threadId),
    queryFn: () => messagingService.getMessageThread(threadId),
    enabled: !!threadId,
  });
}

/**
 * Get messages for an application
 */
export function useApplicationMessages(applicationId) {
  return useQuery({
    queryKey: messagingKeys.application(applicationId),
    queryFn: () => messagingService.getMessagesByApplication(applicationId),
    enabled: !!applicationId,
  });
}

// =============================================================================
// Messages Mutations
// =============================================================================

/**
 * Create a message to client (MarkoTM attorneys only)
 */
export function useCreateClientMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationId, messageData }) =>
      messagingService.createClientMessage(applicationId, messageData),
    onSuccess: (data, { applicationId }) => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.messages() });
      queryClient.invalidateQueries({ queryKey: messagingKeys.application(applicationId) });
    },
  });
}

/**
 * Respond to a message (client)
 */
export function useRespondToMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, responseData }) =>
      messagingService.respondToMessage(messageId, responseData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.messages() });
    },
  });
}

// =============================================================================
// Internal Requests Queries
// =============================================================================

/**
 * Get internal requests queue (MarkoTM attorneyadmin)
 */
export function useInternalRequests(status = 'all') {
  return useQuery({
    queryKey: messagingKeys.internalRequestsList(status),
    queryFn: () => messagingService.getInternalRequests(status),
  });
}

/**
 * Get my internal requests (external attorneys - see their own requests)
 */
export function useMyInternalRequests() {
  return useQuery({
    queryKey: messagingKeys.myRequests(),
    queryFn: () => messagingService.getMyInternalRequests(),
  });
}

/**
 * Get forwarded responses (external attorneys)
 */
export function useForwardedResponses() {
  return useQuery({
    queryKey: messagingKeys.forwardedResponses(),
    queryFn: () => messagingService.getForwardedResponses(),
  });
}

// =============================================================================
// Internal Requests Mutations
// =============================================================================

/**
 * Create internal request (external attorneys)
 */
export function useCreateInternalRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationId, requestData }) =>
      messagingService.createInternalRequest(applicationId, requestData),
    onSuccess: () => {
      // External attorneys might want to see their pending requests
      queryClient.invalidateQueries({ queryKey: messagingKeys.messages() });
    },
  });
}

/**
 * Contact client for internal request (MarkoTM attorneyadmin)
 */
export function useContactClientForRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, messageData }) =>
      messagingService.contactClientForRequest(requestId, messageData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.internalRequests() });
    },
  });
}

/**
 * Forward response to external attorney (MarkoTM)
 */
export function useForwardResponse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, forwardData }) =>
      messagingService.forwardResponseToExternal(requestId, forwardData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.internalRequests() });
    },
  });
}

/**
 * Decline internal request (MarkoTM attorneyadmin)
 */
export function useDeclineInternalRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requestId, reason }) =>
      messagingService.declineInternalRequest(requestId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messagingKeys.internalRequests() });
    },
  });
}

export default {
  // Queries
  useMessages,
  usePendingMessages,
  useMessage,
  useMessageThread,
  useApplicationMessages,
  useInternalRequests,
  useMyInternalRequests,
  useForwardedResponses,

  // Mutations
  useCreateClientMessage,
  useRespondToMessage,
  useCreateInternalRequest,
  useContactClientForRequest,
  useForwardResponse,
  useDeclineInternalRequest,
};
